import { BulkSendMessageResultItem } from '@commenda/api/chatRoom';
import { TBulkMessageJobStatus } from '@commenda/api/job';
import {
  Button,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { IServiceRequest } from '@commenda/types/src/service-request/types';
import { useMemo } from 'react';
import { downloadFailedCsv } from './downloadFailedCsv';

interface FailedStepProps {
  failedResults: BulkSendMessageResultItem[];
  selectedServiceRequests: IServiceRequest[];
  message: string;
  jobStatus: TBulkMessageJobStatus | null;
  onClose: () => void;
}

export const FailedStep = ({
  failedResults,
  selectedServiceRequests,
  message,
  jobStatus,
  onClose,
}: FailedStepProps) => {
  const hasDownloadedFailedCsv = useToggle(false);

  const failedColumns: CustomColumnDef<BulkSendMessageResultItem, unknown>[] = useMemo(() => [
    {
      id: 'serviceRequest',
      accessorKey: 'serviceRequestId',
      header: 'Service Request',
      cell: ({ row }) => {
        const sr = selectedServiceRequests.find((s) => s.id === row.original.serviceRequestId);
        return (
          <div>
            <p className="txt-small-medium">{sr?.corporationName || row.original.serviceRequestId}</p>
            <p className="txt-extra-small-regular text-text-tertiary">{sr?.companyName}</p>
          </div>
        );
      },
    },
    {
      id: 'error',
      accessorKey: 'error',
      header: 'Error',
      cell: ({ row }) => (
        <span className="text-text-error">{row.original.error}</span>
      ),
    },
  ], [selectedServiceRequests]);

  const failedColumnGroups: ColumnGroup<BulkSendMessageResultItem, unknown>[] = useMemo(() => [
    { id: 'main', columns: failedColumns },
  ], [failedColumns]);

  return (
    <>
      <DialogHeader className="flex flex-col gap-xs items-start">
        <DialogTitle>Partial Failure</DialogTitle>
        <DialogDescription>
          {jobStatus?.job.successCount ?? 0} message(s) sent successfully.{' '}
          {jobStatus?.job.failureCount ?? failedResults.length} message(s) failed.
          A CSV file with the failed details has been downloaded.
        </DialogDescription>
      </DialogHeader>

      <div className="px-3xl py-2xl min-w-0 overflow-y-auto flex-1">
        <div className="overflow-x-auto rounded-md border border-border-primary">
          <DataTable
            columnGroups={failedColumnGroups}
            data={failedResults}
          />
        </div>
      </div>

      <DialogFooter>
        <Tooltip content="These details are required for an engineer to debug" asChild>
          <Button
            variant="primary"
            onClick={() => {
              downloadFailedCsv(failedResults, selectedServiceRequests, message);
              hasDownloadedFailedCsv.on();
            }}
          >
            Download Failed Message Details
          </Button>
        </Tooltip>
        <Tooltip content="Downloading failed details is required to enable close" disabled={hasDownloadedFailedCsv.value} asChild>
          <Button variant="secondary" onClick={onClose} disabled={!hasDownloadedFailedCsv.value}>
            Close
          </Button>
        </Tooltip>
      </DialogFooter>
    </>
  );
};
